// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageContext, ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { isEventSearchType, isProductSearchType } from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import EventCard from "@mcmc/App/Widgets/EventsList/EventCard";
import { productListStyles } from "@mcmc/Overrides/Widgets/ProductList/ListCommon.styles";
import Notify from "@mcmc/XDL/Components/Notify";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState, ownProps: OwnProps) => ({
    isLoading: state.pages.productList.isLoading,
    productsDataView: getProductListDataView(state),
    isProductSearch: isProductSearchType(state),
    isEventSearchType: isEventSearchType(state),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasCategoryContext;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: InjectableCss;
    cardList?: CardListStyles;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    notFoundWrapper?: InjectableCss;
}

export const listStyles: ProductListCardListStyles = {};

const styles = productListStyles;

const EventListCardList: FC<Props> = ({ isLoading, productsDataView, isEventSearchType, category }) => {
    if (category && category.properties.isEventsCategory?.toLowerCase() !== "true") {
        return null;
    }

    if (!category && !isEventSearchType) {
        return null;
    }

    if (isLoading && !productsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;

    if (products.length === 0) {
        return (
            <StyledWrapper {...styles.notFoundWrapper}>
                <Notify messageType="danger" hideCloseButton={true} body={siteMessage("McMc_Search_NoEventsFound")} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                <CardList extendedStyles={styles.cardList} data-test-selector="eventListCardList">
                    {products.map(product => (
                        <ProductListProductContext product={product} key={product.id}>
                            <EventCard />
                        </ProductListProductContext>
                    ))}
                </CardList>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(EventListCardList)),
    definition: {
        group: "Product List",
        displayName: "Events List",
        allowedContexts: [ProductListPageContext],
        fieldDefinitions: [],
    },
};

export default widgetModule;
